import { httpClient } from '@shared/infrastructure/http/http-client.js';
import { CONTROL_KIND_TITLES } from '../lib/control-groups.js';
import { isControlKind } from '../lib/group-owner.js';
import type {
  AnalysisConfigDetail,
  AnalysisConfigSummary,
  AnalysisEngines,
  CatalogueGesture,
  CoherenceBindingStatus,
  CoherenceCompareWith,
  CoherenceRuleExample,
  CoherenceRuleOrigin,
  ConfigActivation,
  ConfigBase,
  ConfigExecution,
  ConfigVersion,
  CreateConfigFromBaseInput,
  GestureCatalogue,
  GestureRule,
  RulesetCatalogue,
  SavedConfig,
  StoredConfigVersion,
} from '../model.js';
import { toQueueState } from './analysis-groups.api.js';

/** The three figures of one score method, in the API's spelling. */
interface FiguresPayload {
  incoherences_detected_pct: number | null;
  false_positives_pct: number | null;
  correct_pct: number | null;
  /** «Métricas globales»; absent from an API older than them. */
  incoherences_detected_all_videos_pct?: number | null;
  false_positives_all_videos_pct?: number | null;
}

interface ExecutionPayload {
  /** One of the two: the group's id, or the control's kind (absent from an older gateway). */
  group_id: number | null;
  control_kind?: string | null;
  /** The group's name; null, or absent, for a control, whose name is its title. */
  group_name?: string | null;
  batch: string;
  started_at: string | null;
  finished_at: string | null;
  complete: boolean;
  cancelled: boolean;
  /** Its group's word for where it stands. Absent from an API older than the pause. */
  state?: string | null;
  config_version: number | null;
  threshold_pct: number | null;
  snapshot_id: string | null;
  videos_total: number | null;
  videos_evaluated: number | null;
  methods: Record<string, FiguresPayload> | null;
  /** Absent from a back-office older than it. */
  engines?: string[];
}

interface ConfigPayload {
  id: number;
  name: string;
  description: string | null;
  version: number | null;
  engines_hash: string | null;
  gestures_config_id: string | null;
  is_active: boolean;
  created_at: string | null;
  engines?: AnalysisEngines | null;
}

interface ActivationPayload {
  id: number;
  previous_active_id: number | null;
  changed: boolean;
  activated_at: string | null;
}

interface SavedPayload {
  id: number;
  config_version: {
    gestures_mongo_config_id: string;
    version: number;
    engines_hash: string;
    version_created: boolean;
  } | null;
}

interface VersionPayload {
  id: string;
  version: number;
  created_at: string | null;
  created_by: number | null;
  notes: string;
  engines_hash: string;
  status: 'draft' | 'active' | 'deprecated';
  used_by_runs: number;
  edited_in_place: boolean;
}

interface StoredVersionPayload {
  id: string;
  version: number;
  status: 'draft' | 'active' | 'deprecated';
  engines_hash: string;
  notes: string;
  created_at: string | null;
  created_by: number | null;
  engines: AnalysisEngines | null;
}

interface CataloguePayload {
  gestures: Array<{
    gestureName: string;
    category: string;
    documentLabelEn: string | null;
    documentLabelEs: string | null;
    defaultMethod: 'absolute' | 'baseline';
    defaultApplySustainedRule: boolean;
    defaultDiscardWhileSpeaking: boolean;
    canBeGesture: boolean;
    baselineDependent: boolean;
    tunable?: string[];
    coherenceBinding: {
      status: string;
      ruleIds: string[];
      detail: string | null;
    };
  }>;
  knobBounds?: Record<string, { min: number; max: number; integer: boolean; scope: string }>;
  legacyNames: Record<string, string>;
  rulesetVersion: string;
  unboundRules: string[];
  unknownBoundRules: string[];
}

interface RulesetsPayload {
  defaultVersion: string;
  coherenceRules?: Array<{
    ruleId: string;
    label: string;
    description?: string | null;
    origin: CoherenceRuleOrigin;
    reduction: number;
    enabled?: boolean;
    /** Since coherence 7.3.0, judged rules only. */
    definition?: string | null;
    compareWith?: CoherenceCompareWith | null;
    minConfidence?: number | null;
    examples?: CoherenceRuleExample[] | null;
  }> | null;
  /** Since coherence 7.3.0. */
  coherencePrompt?: string | null;
  /** Since coherence 8.1.0. */
  referenceLimits?: {
    enabled: boolean;
    maxCharsPerReference: number;
    maxOtherVideos: number;
  } | null;
  referenceLimitsBounds?: {
    maxCharsPerReference: { min: number; max: number };
    maxOtherVideos: { min: number; max: number };
  } | null;
  versions: Array<{
    version: string;
    label: string;
    rules: Array<{
      ruleId: string;
      ruleLabel: string;
      humanLabel?: string | null;
      sourceItem: string;
      sourceValue: string;
      reduction: number;
      windowSeconds: number;
      semanticIntents: string[];
      requiresIsSpeaking: boolean | null;
      baselineDependency?: string | null;
    }>;
  }>;
}

export interface SaveConfigInput {
  name: string;
  description?: string | null;
  engines?: AnalysisEngines;
  note?: string;
}

function date(value: string | null): Date | null {
  return value ? new Date(value) : null;
}

/** The request's spelling of a base. */
function baseOf(base: ConfigBase): 'standard' | { copyOf: number } {
  return base.kind === 'standard' ? 'standard' : { copyOf: base.configId };
}

/**
 * One execution, figures kept as they came: a null stays null, because «sin datos» painted as
 * zero is a measurement nobody took.
 */
function toExecution(payload: ExecutionPayload): ConfigExecution {
  const controlKind = isControlKind(payload.control_kind) ? payload.control_kind : null;

  return {
    groupId: controlKind ? null : payload.group_id,
    controlKind,
    groupName:
      payload.group_name ??
      (controlKind ? CONTROL_KIND_TITLES[controlKind] : `Grupo ${payload.group_id ?? ''}`.trim()),
    batch: payload.batch,
    startedAt: date(payload.started_at),
    finishedAt: date(payload.finished_at),
    complete: payload.complete,
    cancelled: payload.cancelled,
    queueState: toQueueState(payload.state),
    configVersion: payload.config_version,
    thresholdPct: payload.threshold_pct,
    snapshotId: payload.snapshot_id,
    videosTotal: payload.videos_total,
    videosEvaluated: payload.videos_evaluated,
    engines: payload.engines ?? [],
    methods: payload.methods
      ? Object.fromEntries(
          Object.entries(payload.methods).map(([method, figures]) => [
            method,
            {
              incoherencesDetectedPct: figures.incoherences_detected_pct ?? null,
              falsePositivesPct: figures.false_positives_pct ?? null,
              correctPct: figures.correct_pct ?? null,
              incoherencesDetectedAllVideosPct:
                figures.incoherences_detected_all_videos_pct ?? null,
              falsePositivesAllVideosPct: figures.false_positives_all_videos_pct ?? null,
            },
          ]),
        )
      : null,
  };
}

function toSummary(payload: ConfigPayload): AnalysisConfigSummary {
  return {
    id: payload.id,
    name: payload.name,
    description: payload.description,
    version: payload.version,
    enginesHash: payload.engines_hash,
    gesturesConfigId: payload.gestures_config_id,
    createdAt: date(payload.created_at),
    runnable: payload.version !== null && payload.gestures_config_id !== null,
    isActive: payload.is_active,
  };
}

/**
 * A binding status, narrowed.
 *
 * The engine may add one, and an unknown value is treated as `unbound`: «no sé si esta regla
 * puntúa» and «no puntúa» look the same on a screen, and of the two guesses the safe one is the
 * pessimistic — it shows a warning instead of hiding one.
 */
function toBindingStatus(value: string): CoherenceBindingStatus {
  return value === 'bound' || value === 'partial' ? value : 'unbound';
}

function toGesture(payload: CataloguePayload['gestures'][number]): CatalogueGesture {
  return {
    gestureName: payload.gestureName,
    category: payload.category,
    labelEn: payload.documentLabelEn,
    labelEs: payload.documentLabelEs,
    defaultMethod: payload.defaultMethod,
    defaultApplySustainedRule: payload.defaultApplySustainedRule,
    defaultDiscardWhileSpeaking: payload.defaultDiscardWhileSpeaking,
    canBeGesture: payload.canBeGesture,
    baselineDependent: payload.baselineDependent,
    // Empty from an engine that does not publish it, and empty is read as «no per-gesture value
    // here»: the editor offers a control only where the engine says it would read one.
    tunable: payload.tunable ?? [],
    bindingStatus: toBindingStatus(payload.coherenceBinding.status),
    ruleIds: payload.coherenceBinding.ruleIds,
    bindingDetail: payload.coherenceBinding.detail,
  };
}

/** One rule on the wire. The same shape in the ruleset catalogue and in a run's sealed snapshot. */
export type RulePayload = RulesetsPayload['versions'][number]['rules'][number];

/**
 * Exported because a run's snapshot carries rules in the catalogue's own spelling, and two
 * translations of one shape would drift the first time the engine adds a field.
 */
export function toRule(payload: RulePayload): GestureRule {
  return {
    ruleId: payload.ruleId,
    ruleLabel: payload.ruleLabel,
    humanLabel: payload.humanLabel ?? null,
    sourceItem: payload.sourceItem,
    sourceValue: payload.sourceValue,
    reduction: payload.reduction,
    windowSeconds: payload.windowSeconds,
    semanticIntents: payload.semanticIntents,
    requiresIsSpeaking: payload.requiresIsSpeaking,
    baselineDependency: payload.baselineDependency ?? null,
  };
}

/**
 * Boundary with the analysis configurations.
 *
 * `runnable` is derived here rather than in the components: every picker needs the same rule, and
 * the rule is that a configuration with no stored version has no engines to run.
 *
 * The two catalogues are read separately from the configurations on purpose. They belong to the
 * engine, they are the same for every configuration, and they are the thing that can be down while
 * everything else works — which is the state HU-38 asks the form to survive by going read-only
 * instead of saving against a guess.
 */
export const analysisConfigsApi = {
  list: async (): Promise<AnalysisConfigSummary[]> => {
    const rows = await httpClient.get<ConfigPayload[]>('/virtual-profiler/analysis-configs');

    return (rows ?? []).map(toSummary);
  },

  get: async (configId: number): Promise<AnalysisConfigDetail> => {
    const payload = await httpClient.get<ConfigPayload>(
      `/virtual-profiler/analysis-configs/${configId}`,
    );

    return { ...toSummary(payload), engines: payload.engines ?? null };
  },

  /**
   * The active configuration, whole: the configuration screen opens on it.
   *
   * `VP_CONFIG_NOT_FOUND` here means that none is active, never that it vanished — the API maps
   * any other 404 to `VP_UNAVAILABLE` — so a screen can tell «no hay» from «no responde».
   */
  active: async (): Promise<AnalysisConfigDetail> => {
    const payload = await httpClient.get<ConfigPayload>(
      '/virtual-profiler/analysis-configs/active',
    );

    return { ...toSummary(payload), engines: payload.engines ?? null };
  },

  /** Makes it the active configuration. A move, not a toggle: there is no switching one off. */
  activate: async (configId: number): Promise<ConfigActivation> => {
    const payload = await httpClient.post<ActivationPayload>(
      `/virtual-profiler/analysis-configs/${configId}/activate`,
    );

    return {
      id: payload.id,
      previousActiveId: payload.previous_active_id,
      changed: payload.changed,
      activatedAt: date(payload.activated_at),
    };
  },

  create: async (input: SaveConfigInput): Promise<SavedConfig> =>
    toSaved(await httpClient.post<SavedPayload>('/virtual-profiler/analysis-configs', input)),

  /**
   * Creates a configuration from «Estándar» or as a copy, and answers it as stored.
   *
   * The same endpoint as `create`, with `base`: the configuration is born with its engines whole
   * and a first version, which is what the detail screen opens on.
   */
  createFromBase: async (input: CreateConfigFromBaseInput): Promise<AnalysisConfigDetail> => {
    const payload = await httpClient.post<ConfigPayload>('/virtual-profiler/analysis-configs', {
      name: input.name,
      description: input.description,
      base: baseOf(input.base),
      ...(input.note ? { note: input.note } : {}),
    });

    return { ...toSummary(payload), engines: payload.engines ?? null };
  },

  /** The executions run with a configuration, newest first, each with its measured figures. */
  executions: async (configId: number): Promise<ConfigExecution[]> => {
    const rows = await httpClient.get<ExecutionPayload[]>(
      `/virtual-profiler/analysis-configs/${configId}/executions`,
    );

    return (rows ?? []).map(toExecution);
  },

  update: async (configId: number, input: SaveConfigInput): Promise<SavedConfig> =>
    toSaved(
      await httpClient.put<SavedPayload>(`/virtual-profiler/analysis-configs/${configId}`, input),
    ),

  remove: (configId: number): Promise<void> =>
    httpClient.delete<void>(`/virtual-profiler/analysis-configs/${configId}`),

  versions: async (configId: number): Promise<ConfigVersion[]> => {
    const rows = await httpClient.get<VersionPayload[]>(
      `/virtual-profiler/analysis-configs/${configId}/versions`,
    );

    return (rows ?? []).map((payload) => ({
      id: payload.id,
      version: payload.version,
      createdAt: date(payload.created_at),
      createdBy: payload.created_by,
      notes: payload.notes,
      enginesHash: payload.engines_hash,
      status: payload.status,
      usedByRuns: payload.used_by_runs,
      editedInPlace: payload.edited_in_place,
    }));
  },

  version: async (configId: number, version: number): Promise<StoredConfigVersion> => {
    const payload = await httpClient.get<StoredVersionPayload>(
      `/virtual-profiler/analysis-configs/${configId}/versions/${version}`,
    );

    return {
      id: payload.id,
      version: payload.version,
      status: payload.status,
      enginesHash: payload.engines_hash,
      notes: payload.notes,
      createdAt: date(payload.created_at),
      createdBy: payload.created_by,
      engines: payload.engines,
    };
  },

  gestureCatalogue: async (): Promise<GestureCatalogue> => {
    const payload = await httpClient.get<CataloguePayload>(
      '/virtual-profiler/analysis-configs/gesture-catalogue',
    );

    return {
      gestures: (payload?.gestures ?? []).map(toGesture),
      knobBounds: payload?.knobBounds ?? {},
      legacyNames: payload?.legacyNames ?? {},
      rulesetVersion: payload?.rulesetVersion ?? '',
      unboundRules: payload?.unboundRules ?? [],
      unknownBoundRules: payload?.unknownBoundRules ?? [],
    };
  },

  rulesets: async (): Promise<RulesetCatalogue> => {
    const payload = await httpClient.get<RulesetsPayload>(
      '/virtual-profiler/analysis-configs/rulesets',
    );

    return {
      defaultVersion: payload?.defaultVersion ?? '',
      versions: (payload?.versions ?? []).map((version) => ({
        version: version.version,
        label: version.label,
        rules: version.rules.map(toRule),
      })),
      // Absent from a Virtual Profiler older than the coherence rules: none, and the map says so.
      coherenceRules: (payload?.coherenceRules ?? []).map((rule) => ({
        ruleId: rule.ruleId,
        label: rule.label,
        description: rule.description ?? '',
        origin: rule.origin,
        reduction: rule.reduction,
        enabled: rule.enabled !== false,
        // The engine's texts of a judged rule (7.3.0): what «Restaurar la del motor» copies.
        ...(rule.origin === 'gesture'
          ? {}
          : {
              ...(typeof rule.definition === 'string' ? { definition: rule.definition } : {}),
              ...(rule.compareWith ? { compareWith: rule.compareWith } : {}),
              minConfidence: rule.minConfidence ?? null,
              examples: (rule.examples ?? []).map((example) => ({
                ...example,
              })),
            }),
      })),
      // Absent from an engine older than 7.3.0: nothing to restore.
      coherencePrompt: payload?.coherencePrompt ?? '',
      ...(payload?.referenceLimits ? { referenceLimits: { ...payload.referenceLimits } } : {}),
      ...(payload?.referenceLimitsBounds
        ? {
            referenceLimitsBounds: {
              maxCharsPerReference: {
                ...payload.referenceLimitsBounds.maxCharsPerReference,
              },
              maxOtherVideos: {
                ...payload.referenceLimitsBounds.maxOtherVideos,
              },
            },
          }
        : {}),
    };
  },
};

function toSaved(payload: SavedPayload): SavedConfig {
  const version = payload.config_version;

  return {
    id: payload.id,
    version: version
      ? {
          gesturesConfigId: version.gestures_mongo_config_id,
          version: version.version,
          enginesHash: version.engines_hash,
          versionCreated: version.version_created,
        }
      : null,
  };
}
